import { Reducer, combineReducers, configureStore } from '@reduxjs/toolkit';
import userReducer from '@/features/user/userSlice';
import assetReducer from '@/features/asset/assetSlice';
import filterSliceReducer from '@/features/filters/filtersSlice';
import { apiBase } from '@/features/api/apiBase';
import { getConfig } from '@/constants';
import { FiltersState, UserState } from '@/types/types';

import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import { localPersistStorage } from './persistLocalStorage';

const persistConfig = {
  key: 'root',
  storage: localPersistStorage,
};

const persistFilterConfig = {
  key: 'rootFilter',
  storage: localPersistStorage,
};

const persistedReducer = persistReducer<UserState>(persistConfig, userReducer);
const persistedFiltersReducer = persistReducer<FiltersState>(
  persistFilterConfig,
  filterSliceReducer,
);

const reducers = {
  user: persistedReducer,
  asset: assetReducer,
  filters: persistedFiltersReducer,
  [apiBase.reducerPath]: apiBase.reducer,
};

const combinedReducer = combineReducers<typeof reducers>(reducers);

export const rootReducer: Reducer<RootState> = (state, action) => {
  return combinedReducer(state, action);
};

export const setupStore = (preloadedState: any) =>
  configureStore({
    reducer: rootReducer,
    preloadedState,
    // ? show the devTools only in development and in non-playwright mode
    devTools:
      process.env.NODE_ENV === 'development' && !getConfig().IS_PLAYWRIGHT,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat([apiBase.middleware]),
  });

export const observeStore = (store: AppStore, select: any, onChange: any) => {
  let currentState: any;

  function handleChange() {
    const nextState: UserState = select(store.getState());
    if (nextState !== currentState) {
      currentState = nextState;
      onChange(currentState);
    }
  }

  const unsubscribe: any = store.subscribe(handleChange);
  handleChange();
  return unsubscribe;
};

export const store = setupStore({});
export const persistor = persistStore(store);
export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;
export type RootState = ReturnType<typeof combinedReducer>;
